/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { Card, Row, Col, Button, message } from "antd";

import { DownloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { ReportsService } from "../../../../services/api/reports";
import { getHubList } from "../../../../services/api/hub";
import { ZONE_OPTIONS } from "../../../../constants/reportConstants";
import { downloadHubwiseShipmentsInscanVsOutscanReport } from "../../../../utils/downloadUtils";
import moment from "moment";

const HubwiseShipmentsInscanVsOutscanReport = () => {
    const FILTER_STORAGE_KEY = "SHIPMENTS_INSCAN_VS_OUTSCAN_REPORT_FILTERS";

    // Global request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [reportData, setReportData] = useState([]);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [selectedZones, setSelectedZones] = useState([]);
    const [hubLoading, setHubLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [hubUser, setHubUser] = useState();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);

    // Helper function to save filters to sessionStorage
    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange ? [
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            ] : null,
            selectedHubs,
            selectedZones,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    // Helper function to restore filters from sessionStorage
    const restoreFiltersFromStorage = () => {
        try {
            const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                    setDateRange([
                        moment(filters.dateRange[0]),
                        moment(filters.dateRange[1])
                    ]);
                }
                if (filters.selectedHubs && filters.selectedHubs.length > 0) {
                    setSelectedHubs(filters.selectedHubs);
                }
                if (filters.selectedZones && filters.selectedZones.length > 0) {
                    setSelectedZones(filters.selectedZones);
                }
                if (filters.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        current: filters.pagination.current || prev.current,
                        pageSize: filters.pagination.pageSize || prev.pageSize
                    }));
                }
                // Clear the saved filters after restoring
                sessionStorage.removeItem(FILTER_STORAGE_KEY);
                return true;
            }
        } catch (error) {
            console.error("Error restoring filters:", error);
        }
        return false;
    };
    // Initial load - fetch hub list
    useEffect(() => {
        fetchHubList();
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        let userid = JSON.parse(localStorage.getItem("userId") || "{}");
        setHubUser(user?.roles?.[0]);
        const activeHub = JSON.parse(localStorage.getItem("active_hub"));
        const activeHubId = activeHub?.hubId;

        // Check if there are saved filters - if yes, don't set default hub (restore will handle it)
        const hasSavedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
        // Only set default hub if no hubs are selected and no filters were saved (to avoid overriding restored filters)
        if (hubList.length > 0 && selectedHubs.length === 0 && !hasSavedFilters) {
            if (activeHubId) {
                const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
                if (defaultHub) {
                    setSelectedHubs([defaultHub.value]);
                    selectHubHandle([defaultHub.value]);
                }
            } else if (user?.roles?.[0] === "ROLE_HUBADMIN") {
                const defaultHub = hubList.find(hub => String(hub.value) === String(userid?.userInfo?.premiseId));
                if (defaultHub) {
                    setSelectedHubs([defaultHub.value]);
                    selectHubHandle([defaultHub.value]);
                }
            }
        }
    }, [hubList]);

    // Debounced effect for report fetching
    useEffect(() => {
        if (!hubUser) return; // Wait for hubUser to be set
        if (hubUser === "ROLE_HUBADMIN" && selectedHubs.length === 0) return; // Only block for hubadmin
        if (!dateRange[0] || !dateRange[1]) return; // Always require date range
        const timer = setTimeout(() => {
            fetchHubwiseShipmentsInscanVsOutscanReport();
        }, 300);

        return () => clearTimeout(timer);
    }, [hubUser, selectedHubs, selectedZones, dateRange, pagination.current, pagination.pageSize]);

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const columns = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            sorter: true,
            width: 120,
        },
        {
            title: "Hub Name",
            dataIndex: "hub",
            key: "hub",
            sorter: true,
            width: 200,
        },
        {
            title: "Zone",
            dataIndex: "zone",
            key: "zone",
            sorter: true,
            width: 120,
        },
        {
            title: "State",
            dataIndex: "state",
            key: "state",
            sorter: true,
            width: 120,
        },
        {
            title: "Total Inscan Shipments",
            dataIndex: "totalInscanShipments",
            key: "totalInscanShipments",
            sorter: true,
            width: 180,
            render: (text, record) => (
                text && text > 0 ? (
                    <span
                        style={{
                            color: "#1890ff",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: "bold"
                        }}
                        onClick={() => {
                            // Save current filters before navigating
                            saveFiltersToStorage();

                            navigate("/shipment-inscan-clickable-report", {
                                state: {
                                    operationDate: record.date,
                                    premiseId: record.hubId || record.premiseId || record.id
                                }
                            });
                        }}
                    >
                        {text?.toLocaleString() || 0}
                    </span>
                ) : <span>{text?.toLocaleString() || 0}</span>
            ),
        },
        {
            title: "Total Inscan Weight (kg)",
            dataIndex: "totalInscanShipmentsWeight",
            key: "totalInscanShipmentsWeight",
            sorter: true,
            width: 180,
            render: (text) => text ? parseFloat(text).toFixed(2) : "0.00",
        },
        {
            title: "Total Outscan Shipments",
            dataIndex: "totalOutscanShipments",
            key: "totalOutscanShipments",
            sorter: true,
            width: 180,
            render: (text, record) => (
                text && text > 0 ? (
                    <span
                        style={{
                            color: "#1890ff",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: "bold"
                        }}
                        onClick={() => {
                            // Save current filters before navigating
                            saveFiltersToStorage();

                            navigate("/shipment-outscan-clickable-report", {
                                state: {
                                    operationDate: record.date,
                                    premiseId: record.hubId || record.premiseId || record.id
                                }
                            });
                        }}
                    >
                        {text?.toLocaleString() || 0}
                    </span>
                ) : <span>{text?.toLocaleString() || 0}</span>
            ),
        },
        {
            title: "Total Outscan Weight (kg)",
            dataIndex: "totalOutscanShipmentsWeight",
            key: "totalOutscanShipmentsWeight",
            sorter: true,
            width: 180,
            render: (text) => text ? parseFloat(text).toFixed(2) : "0.00",
        },
        {
            title: "Pending Shipments",
            dataIndex: "pendingShipments",
            key: "pendingShipments",
            sorter: true,
            width: 180,
            render: (text, record) => (
                text && text > 0 ? (
                    <span
                        style={{
                            color: "#1890ff",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: "bold"
                        }}
                        onClick={() => {
                            // Save current filters before navigating
                            saveFiltersToStorage();

                            navigate("/shipment-pending-clickable-report", {
                                state: {
                                    operationDate: record.date,
                                    premiseId: record.hubId || record.premiseId || record.id
                                }
                            });
                        }}
                    >
                        {text?.toLocaleString() || 0}
                    </span>
                ) : <span>{text?.toLocaleString() || 0}</span>
            ),
        },
    ];

    const selectHubHandle = (values) => {
        setSelectedHubs(values || []); // Always allow updating selectedHubs
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates || []); // Handle undefined when cleared
    };

    const handleDownload = async () => {
        if (!dateRange[0] || !dateRange[1]) {
            message.error("Please select a date range for download");
            return;
        }

        setDownloadLoading(true);
        try {
            const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
            const selectedZoneData = ZONE_OPTIONS.filter(zone => selectedZones?.includes(zone.value));

            const downloadParams = {
                ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
                ...(selectedZoneData?.length > 0 && { zones: selectedZoneData.map(zone => zone.value) }),
                fromDate: dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : "",
                toDate: dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : ""
            };

            await downloadHubwiseShipmentsInscanVsOutscanReport(downloadParams);
            message.success("Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    // All API Call

    //Get Hub List
    const fetchHubList = async () => {
        setHubLoading(true);
        try {
            const hubData = await getHubList();
            setHubList(hubData);
            // Restore filters after hub list is loaded
            restoreFiltersFromStorage();
        } catch (error) {
            message.error("Failed to fetch hub list");
            setHubList([]);
        } finally {
            setHubLoading(false);
        }
    };


    //this for fetch report
    const fetchHubwiseShipmentsInscanVsOutscanReport = async () => {
        // Only block for hubadmin with no hub or if date range is not set
        const user = JSON.parse(localStorage.getItem("userinfo"));
        const isHubAdmin = user?.roles?.[0] === "ROLE_HUBADMIN";
        if (!dateRange[0] || !dateRange[1] || (isHubAdmin && selectedHubs.length === 0)) {
            setReportData([]);
            setPagination(prev => ({ ...prev, total: 0 }));
            return;
        }

        // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
        const { signal, requestId } = createNewRequest("hubwise-shipments-inscan-vs-outscan-report");

        try {
            setLoading(true);

            const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
            const selectedZoneData = ZONE_OPTIONS.filter(zone => selectedZones?.includes(zone.value));

            // Only include non-empty arrays in params
            const params = {
                ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
                ...(selectedZoneData?.length > 0 && { zones: selectedZoneData.map(zone => zone.value) }),
                ...(dateRange?.[0] && dateRange?.[1] && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                page: pagination.current - 1, // Convert to 0-based for API
                size: pagination.pageSize
            };

            const { data, total, page, pageSize, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getHubwiseShipmentsInscanVsOutscanReport(params, {
                signal
            });

            // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
            if (isLatestRequest(requestId)) {
                console.log(`🔥 Request ${requestId} completed successfully - updating state`);
                console.log("Report data sample:", data[0]); // Debug: log first record to see structure
                setReportData(data);
                setLastsynced(lastRefreshedAt);
                setNextScheduledRefresh(nextScheduledRefresh);
                setPagination(prev => ({
                    ...prev,
                    total,
                }));
            }

        } catch (error) {
            // 🔥 DON'T UPDATE STATE IF REQUEST WAS CANCELLED
            if (error.name === "AbortError") {
                console.log("🔥 Request was cancelled at HTTP level - no state update");
                return;
            }

            // Only update if this is still the latest request
            if (isLatestRequest(requestId)) {
                message.error("Failed to fetch report data");
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
            }
        } finally {
            // 🔥 ONLY UPDATE LOADING IF THIS IS THE LATEST REQUEST
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading
                    title="Shipments Inscan vs Outscan Report"
                    navigateTo="/reports"
                >
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From & To Date</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Select Hubs</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Hubs"
                            options={hubList}
                            onChange={(value) => {
                                selectHubHandle(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedHubs}
                            allowClear
                            mode={hubUser !== "ROLE_HUBADMIN" ? "multiple" : "default"}
                            disabled={hubUser === "ROLE_HUBADMIN"}
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                            loading={hubLoading}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Select Zones</div>
                        <DropDownComponent
                            placeholder="Select Zones"
                            options={ZONE_OPTIONS}
                            onChange={(value) => {
                                setSelectedZones(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedZones}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} style={{ textAlign: "right", paddingTop: "22px" }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                            disabled={!dateRange[0] || !dateRange[1]}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>

            </Card>
            <TableComponent
                columns={columns}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
                sticky
            />
        </div>
    );
};

export default HubwiseShipmentsInscanVsOutscanReport;
